/**
 * SmartImportDropzone — Quick drag-and-drop zone for the Dashboard.
 * Auto-detects broker from filename, shows parsed summary,
 * and navigates to the Importar Dados tab for confirmation.
 *
 * Sprint 11 (B1): Eliminates 3 clicks per import by allowing
 * direct file drop on the Dashboard.
 */
import { useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useToast } from "../../components/Toast.jsx";
import { RADIUS, TRANSITION } from "../../shared/theme.js";

// Must match extratoParser's detectBrokerFromFilename logic
function detectBroker(filename) {
  const f = (filename || "").toLowerCase();
  if (f.includes("avenue") || f.includes("stmt_")) return { code: "avenue", label: "Avenue" };
  if (f.includes("xp")) return { code: "xp", label: "XP Investimentos" };
  if (f.includes("btg")) return { code: "btg", label: "BTG Pactual" };
  if (f.includes("rentabilidade") || (f.includes("relatorio") && f.includes("inter")))
    return { code: "interwin", label: "Inter / Interwin" };
  if (f.includes("inter")) return { code: "interwin", label: "Inter / Interwin" };
  return null;
}

const ACCEPTED = ".csv,.xlsx,.xls,.ofx,.pdf";
const MAX_SIZE = 50 * 1024 * 1024; // 50 MB — matches storage.ts & client UI

export default function SmartImportDropzone({ onFileReady, T }) {
  const [dragOver, setDragOver] = useState(false);
  const [detected, setDetected] = useState(null); // { file, broker }
  const fileRef = useRef();
  const toast = useToast();

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      if (file.size > MAX_SIZE) {
        toast("Arquivo excede 50 MB.", "error");
        return;
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!["csv", "xlsx", "xls", "ofx", "pdf"].includes(ext)) {
        toast("Formato não suportado. Use CSV, XLSX, XLS, OFX ou PDF.", "error");
        return;
      }
      const broker = detectBroker(file.name);
      if (broker) {
        setDetected({ file, broker });
      } else {
        // Can't auto-detect — navigate to full import tab
        setDetected({ file, broker: null });
      }
    },
    [toast]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      handleFile(file);
    },
    [handleFile]
  );

  const handleConfirm = () => {
    if (detected?.file) {
      onFileReady(detected.file, detected.broker?.code || null);
      setDetected(null);
    }
  };

  const handleCancel = () => {
    setDetected(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !detected && fileRef.current?.click()}
      style={{
        border: `2px dashed ${dragOver ? T.accent : T.border}`,
        borderRadius: RADIUS.lg || 12,
        padding: detected ? "16px 20px" : "20px 24px",
        textAlign: "center",
        cursor: detected ? "default" : "pointer",
        background: dragOver ? T.infoBg || "rgba(37,99,235,0.06)" : "transparent",
        transition: TRANSITION.base || "all 0.2s ease",
        marginBottom: 16,
      }}
    >
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED}
        style={{ display: "none" }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {!detected ? (
        <div>
          <span style={{ fontSize: 22, display: "block", marginBottom: 4 }}>📥</span>
          <p style={{ fontSize: 12, fontWeight: 600, color: T.text, margin: 0 }}>
            Arraste um extrato aqui ou clique para importar
          </p>
          <p style={{ fontSize: 11, color: T.muted, margin: "4px 0 0" }}>
            XP, BTG, Inter, Avenue — CSV, XLSX, PDF (até 50 MB)
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}>
          <span style={{ fontSize: 20 }}>📄</span>
          <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: T.text,
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {detected.file.name}
            </p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>
              {detected.broker
                ? `Detectado: ${detected.broker.label}`
                : "Corretora não detectada — selecionar manualmente"}
            </p>
          </div>
          <button
            onClick={handleConfirm}
            style={{
              padding: "6px 14px",
              borderRadius: 8,
              border: "none",
              background: T.accent,
              color: T.accentText,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {detected.broker ? "Importar →" : "Selecionar corretora →"}
          </button>
          <button
            onClick={handleCancel}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              background: "transparent",
              color: T.sub,
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

SmartImportDropzone.propTypes = {
  onFileReady: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};
